import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { LoggerService } from 'src/services/logger-services/logger.service';

@Pipe({
  name: 'sanitizeMarkdown',
  pure: true
})
export class SanitizeMarkdownPipe implements PipeTransform {
  private readonly SanitizeMarkdownPipeError = (error: any)=>
    `[SanitizeMarkdownPipe] ${JSON.stringify(error)}`;
  
  // Store HTML tags during preprocessing for restoration after markdown parsing
  private readonly htmlTagStore: Map<string, string> = new Map();
  private htmlPlaceholderCounter: number = 0;
  
  constructor(private readonly sanitizer: DomSanitizer,
    private readonly loggerService: LoggerService
  ) {}

  transform(value?: string): SafeHtml {
    try{
    if (!value) return '';

    // Clear HTML tag store for this transformation
    this.htmlTagStore.clear();
    this.htmlPlaceholderCounter = 0;

    // Step 1: Extract LaTeX into placeholders
    const latexRegex = /\$\$[\s\S]+?\$\$|\\{1,2}\[[\s\S]+?\\{1,2}\]|\\{1,2}\([\s\S]+?\\{1,2}\)/g;
    const equations: { placeholder: string; html: string }[] = [];
    let counter = 0;
    value = this.convertBase64Images(value);
    value = this.convertFlagEmojis(value);
    
    // Escape "N)" at line start so marked does not parse as ordered list (which would reset to "1." per block).
    // This preserves display as "1)", "2)", "3)" etc. without affecting "1.", "2." style lists.
    value = this.escapeParenthesisNumberedLines(value);
    // Ensure a blank line before indented bullet lists that follow "N)" lines so they parse as lists.
    value = this.ensureBlankLineBeforeIndentedBulletsAfterParenthesisLines(value);
    // Ensure a blank line after bullet lists before "N)" so 10), 11), etc. are not parsed as list items.
    value = this.ensureBlankLineAfterBulletListBeforeParenthesisNumber(value);
    
    // Step 1.5: Pre-process markdown tables to handle HTML tags properly
    value = this.preprocessTablesWithHtmlTags(value);
    
        let temp = value.replace(latexRegex, (match: string) => {
      // Determine delimiters and extract content
      let equation = '';
      let isBlock = false;

      const blockMatch = /^\$\$([\s\S]+?)\$\$$/.exec(match);
      const displayMatch = /^\\{1,2}\[([\s\S]+?)\\{1,2}\]$/.exec(match);
      const inlineMatch = /^\\{1,2}\(([\s\S]+?)\\{1,2}\)$/.exec(match);

      if (blockMatch) {
        equation = blockMatch[1];
        isBlock = true;
      } else if (displayMatch) {
        equation = displayMatch[1];
        isBlock = true;
      } else if (inlineMatch) {
        equation = inlineMatch[1];
        isBlock = false;
      } else {
        // Fallback: strip outer most delimiters generically
        equation = match.replace(/^(.)([\s\S]+)(.)$/s, (_m, _a, inner) => inner) || match;
      }

      let rendered = '';
      try {
        rendered = katex.renderToString(equation, {
          throwOnError: false,
          displayMode: isBlock,
        });
      } catch {
        rendered = match; // fallback to raw
      }

      const placeholder = `@@LATEX_${counter++}@@`;
      equations.push({ placeholder, html: rendered });
      return placeholder;
    });

    // Step 2: Run Markdown on the text (no LaTeX left, just placeholders)
    let html = marked(temp, { 
      breaks: true,
      gfm: true, // GitHub Flavored Markdown for better table support
      tables: true // Explicitly enable tables
    });

    // Step 2.5: Restore ALL protected HTML tags that were protected during table processing
    html = this.restoreProtectedHtmlTags(html);

    // Step 3: Put KaTeX HTML back
    equations.forEach(eq => {
      html = html.replace(eq.placeholder, eq.html);
    });

    // Step 4: Sanitize HTML with DOMPurify to prevent XSS attacks
    // NOSONAR: bypassSecurityTrustHtml is safe - content sanitized by DOMPurify first
    // Note: KaTeX HTML is XSS-safe by design, so we allow its required elements/attributes
    const cleanHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                     'ul', 'ol', 'li', 'a', 'img', 'code', 'pre', 'blockquote', 'table',
                     'thead', 'tbody', 'tr', 'th', 'td', 'span', 'div', 'hr',
                     // MathML elements required by KaTeX
                     'math', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'msqrt', 'mroot',
                     'mover', 'munder', 'munderover', 'mtable', 'mtr', 'mtd', 'mrow',
                     'menclose', 'mtext', 'mspace', 'mpadded', 'mstyle', 'merror', 'maction'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'target', 'rel', 'draggable', 'style',
                     'id', 'aria-label', 'aria-hidden', 'role'],
      ALLOW_DATA_ATTR: true, // KaTeX uses data attributes for accessibility
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    });

    // Step 5: Return SafeHtml (now safe after DOMPurify sanitization)
    return this.sanitizer.bypassSecurityTrustHtml(cleanHtml); // NOSONAR: Safe - DOMPurify sanitized
  }
  catch(error: any){
    this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "transform")
    return '';
  }
  }

  /**
   * Escapes "N)" at the start of a line so the markdown parser does not treat it as an ordered list.
   * Marked/CommonMark treats "1)", "2)" as list markers but creates separate <ol> blocks (each showing "1.").
   * By escaping the closing paren (e.g. "1\)") we get literal "1)", "2)", "3)" in the output.
   * Does not affect "1.", "2." style lists (period form).
   */
  private escapeParenthesisNumberedLines(text: string): string {
    try {
      return text.replace(/(^|\n)(\d+)\)/gm, (_match, prefix, num) => prefix + num + '\\)');
    } catch (error: any) {
      this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "escapeParenthesisNumberedLines");
      return text;
    }
  }

  /**
   * Ensures a blank line before indented bullet lines (- ) that immediately follow an "N)" line.
   * Without this, the parser treats the bullets as part of the same paragraph and they don't render as a list.
   * Implemented with line-by-line logic to avoid ReDoS from backtracking in regex.
   */
  private ensureBlankLineBeforeIndentedBulletsAfterParenthesisLines(text: string): string {
    try {
      const lines = text.split(/\r?\n/);
      const result: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        result.push(lines[i]);
        if (i < lines.length - 1 && this.lineContainsEscapedParenthesisNumber(lines[i]) && this.isIndentedBulletLine(lines[i + 1])) {
          result.push('');
        }
      }
      return result.join('\n');
    } catch (error: any) {
      this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "ensureBlankLineBeforeIndentedBulletsAfterParenthesisLines");
      return text;
    }
  }

  /** True if line contains digit(s) followed by escaped paren "\)" (string scan, no regex ReDoS). */
  private lineContainsEscapedParenthesisNumber(line: string): boolean {
    const idx = line.indexOf('\)');
    return idx >= 1 && line[idx - 1] >= '0' && line[idx - 1] <= '9';
  }

  /** True if line starts with optional whitespace then "- " (string check, no regex ReDoS). */
  private isIndentedBulletLine(line: string): boolean {
    const trimmed = line.trimStart();
    return line.length > 0 && line !== trimmed && trimmed.startsWith('- ');
  }

  /**
   * Ensures a blank line after a bullet line when the next line is "N)" (e.g. 10), 11)).
   * Without this, 10), 11), etc. are parsed as list items in the same ul and appear indented.
   * Implemented with line-by-line logic to avoid ReDoS from backtracking in regex.
   */
  private ensureBlankLineAfterBulletListBeforeParenthesisNumber(text: string): string {
    try {
      const lines = text.split(/\r?\n/);
      const result: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        result.push(lines[i]);
        if (i < lines.length - 1 && this.isBulletLine(lines[i]) && this.lineContainsEscapedParenthesisNumber(lines[i + 1])) {
          result.push('');
        }
      }
      return result.join('\n');
    } catch (error: any) {
      this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "ensureBlankLineAfterBulletListBeforeParenthesisNumber");
      return text;
    }
  }

  /** True if line contains whitespace, "- ", then whitespace (bullet pattern; string scan, no regex ReDoS). */
  private isBulletLine(line: string): boolean {
    const i = line.indexOf('-');
    return i >= 1 && i < line.length - 1 && this.isWhitespace(line[i - 1]) && this.isWhitespace(line[i + 1]);
  }

  private isWhitespace(c: string): boolean {
    return c === ' ' || c === '\t' || c === '\r' || c === '\n' || c === '\f' || c === '\v';
  }

  /**
   * Pre-processes markdown tables that contain HTML tags to prevent parsing issues.
   * The marked library has trouble parsing tables with ANY HTML tags in cells.
   * This method protects ALL HTML tags by converting them to placeholders before parsing,
   * then restoring them after.
   * 
   * Handles: <br>, <strong>, <em>, <b>, <i>, <u>, <code>, <span>, <a>, <img>, and ALL other HTML tags
   */
  private preprocessTablesWithHtmlTags(text: string): string {
    try {
      const lines = text.split(/\r?\n/);
      const result: string[] = [];
      let i = 0;

      while (i < lines.length) {
        // Detect start of a markdown table row using simple string checks (no ReDoS risk)
        if (this.isTableRow(lines[i])) {
          const tableLines: string[] = [];
          
          // Collect consecutive table rows
          while (i < lines.length && this.isTableRow(lines[i])) {
            tableLines.push(lines[i]);
            i++;
          }
          
          // Process table safely - protect HTML tags in each row
          const processedTableLines = tableLines.map(row => {
            const processed = this.protectHtmlTagsInRow(row);
            return processed;
          });
          
          result.push(processedTableLines.join('\n'));
        } else {
          result.push(lines[i]);
          i++;
        }
      }

      return result.join('\n');
    } catch (error: any) {
      this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "preprocessTablesWithHtmlTags");
      return text; // Return original text on error
    }
  }

  /**
   * Check if a line is a markdown table row
   * 
   * @param line - Line to check
   * @returns true if line is a table row
   */
  private isTableRow(line: string): boolean {
    // Simple, non-backtracking check for table rows
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|');
  }

  /**
   * Protect HTML tags in a table row by replacing them with placeholders
   * 
   * SECURITY: The regex /<\/?[a-zA-Z][^>]*>/gi is safe from ReDoS because:
   * - [^>]* is bounded by the required > character (atomic match, no backtracking)
   * - No nested quantifiers or alternation that could cause exponential time complexity
   * - Linear time complexity O(n) where n is the input length
   * 
   * @param row - Table row to process
   * @returns Row with HTML tags replaced by placeholders
   */
  private protectHtmlTagsInRow(row: string): string {
    return row.replace(/<\/?[a-zA-Z][^>]*>/gi, (htmlTag) => {
      const placeholder = `{{HTMLTAG${this.htmlPlaceholderCounter}}}`;
      this.htmlTagStore.set(placeholder, htmlTag);
      this.htmlPlaceholderCounter++;
      return placeholder;
    });
  }

  /**
   * Restores ALL HTML tags that were protected during table preprocessing
   * This iterates through the htmlTagStore and replaces placeholders with original HTML tags
   */
  private restoreProtectedHtmlTags(html: string): string {
    try {
      // Restore all HTML tag placeholders back to their original HTML tags
      let result = html;
      
      this.htmlTagStore.forEach((originalTag, placeholder) => {
        // Use global replace to handle cases where the same placeholder appears multiple times
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), originalTag);
      });
      
      return result;
    } catch (error: any) {
      this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "restoreProtectedHtmlTags");
      return html;
    }
  }

  private convertFlagEmojis(text: string): string {
    try{
    const flagEmojiRegex = /(?:[\u{1F1E6}-\u{1F1FF}][\u{1F1E6}-\u{1F1FF}]|\u{1F3F4}\u{E0067}\u{E0062}[\u{E0065}\u{E0073}\u{E0077}][\u{E0065}-\u{E0073}]*\u{E007F})/gu;

    return text.replace(flagEmojiRegex, (match) => {
      const codepoints = Array.from(match)
        .map(char => char.codePointAt(0)?.toString(16).padStart(4, '0'))
        .join('-');

      return `<img class="flag-emoji" src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/${codepoints}.png" alt="${match}" draggable="false">`;
    });
  }
  catch(error: any){
    this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "convertFlagEmojis")
    return '';
  }
  }

  private convertBase64Images(text: string): string {
    try{
    const base64ImageRegex = /(?:!\[([^\]]*)\]\(data:image\/(png|jpeg|jpg|gif|webp);base64,([^)]+)\)|data:image\/(png|jpeg|jpg|gif|webp);base64,([^\s\n\r]+))/gi;
    const simpleBase64Regex = /data:image\/(png|jpeg|jpg|gif|webp);base64,([^\s\n\r]+)/gi;

    let result = text.replace(base64ImageRegex, (match, altText, format1, base64Data1, format2, base64Data2) => {
      const alt = altText || 'Generated Image';
      const format = format1 || format2 || 'png';
      const base64 = base64Data1 || base64Data2;

      if (!this.isValidBase64(base64)) return match;
      return `<img class="base64-image" src="data:image/${format};base64,${base64}" alt="${alt}">`;
    });

    if (result === text) {
      result = text.replace(simpleBase64Regex, (match, format, base64) => {
        if (!this.isValidBase64(base64)) return match;
        return `<img class="base64-image" src="data:image/${format};base64,${base64}" alt="Generated Image">`;
      });
    }

    return result;
  }
  catch(error: any){
    this.loggerService.logError(this.SanitizeMarkdownPipeError(error), "convertBase64Images")
    return '';
  }
  }

  private isValidBase64(str: string): boolean {
    try {
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) return false;
      if (str.length % 4 !== 0) return false;
      const testStr = str.substring(0, Math.min(100, str.length));
      atob(testStr);
      return true;
    } catch {
      this.loggerService.logError(this.SanitizeMarkdownPipeError("isValidBase64"), "isValidBase64")
      return false;
    }
  }
}
